import { FilePdfOutlined } from "@ant-design/icons";
import { Image } from "antd";
import type { CSSProperties } from "react";
import { useMemo } from "react";
import { resolveAppMediaUrl } from "../../utils/resolveAppMediaUrl";
import { stripOssImageProcess } from "fin-web-components";
import { CsMessageTextContent } from "../../space/customer-service/CsMessageTextContent";
import { COMPANION_MSG_QUOTE, COMPANION_MSG_THUMB } from "./Config";
import { companionIsSelfModerationRejected, type CompanionMessageRow } from "./CompanionRoomType";
import type { ChatMentionProduct } from "./chatMentionUtil";
import { renderChatMentionText } from "./chatMentionUtil";

function displayFileName(m: CompanionMessageRow): string {
  const n = m.fileName?.trim();
  if (n) return n;
  if (m.messageType === "PDF") return "PDF文件";
  if (m.messageType === "IMAGE") return "图片";
  if (m.messageType === "STICKER") return "表情";
  return "";
}

function moderationHint(m: CompanionMessageRow): string | null {
  if (companionIsSelfModerationRejected(m)) return null;
  const s = m.moderationStatus?.trim();
  if (s === "REJECTED") return "审核未通过";
  return null;
}

export function quotePreviewText(m: CompanionMessageRow): string {
  if (Boolean(m.deleted)) return "引用内容已撤回";
  if (Boolean(m.shielded)) return "引用内容已屏蔽";
  if (m.messageType === "IMAGE") return "[图片]";
  if (m.messageType === "STICKER") return "[表情]";
  if (m.messageType === "PDF") return m.fileName?.trim() || "[PDF]";
  const t = (m.content || "").trim();
  return t || "消息";
}

/**
 * 微信式引用：挂在气泡外下方。
 * 自己消息：右对齐 + 右侧竖线；别人消息：左对齐 + 左侧竖线。
 * 文案：姓名: 内容
 */
export function CompanionReplyQuoteBlock(props: {
  name: string;
  preview: string;
  /** 当前这条消息是否自己发出（决定竖线在左还是右） */
  alignSelf?: boolean;
}): JSX.Element {
  const rail = `${COMPANION_MSG_QUOTE.railWidth}px solid ${COMPANION_MSG_QUOTE.railColor}`;
  const style: CSSProperties = {
    fontSize: COMPANION_MSG_QUOTE.fontSize,
    color: COMPANION_MSG_QUOTE.color,
    maxWidth: COMPANION_MSG_QUOTE.width,
    marginTop: COMPANION_MSG_QUOTE.marginTop,
    ...(props.alignSelf
      ? {
          borderRight: rail,
          paddingRight: COMPANION_MSG_QUOTE.railGap,
          borderLeft: 0,
          paddingLeft: 0,
        }
      : {
          borderLeft: rail,
          paddingLeft: COMPANION_MSG_QUOTE.railGap,
          borderRight: 0,
          paddingRight: 0,
        }),
  };
  return (
    <div
      className={`h5-crm-msg-quote${props.alignSelf ? " h5-crm-msg-quote--self" : ""}`}
      style={style}
    >
      <span className="h5-crm-msg-quote-text">
        {props.name}: {props.preview}
      </span>
    </div>
  );
}

export function CompanionRoomMessageContent(props: {
  m: CompanionMessageRow;
  onOpenPdf?: (url: string, name: string) => void;
  mentionProducts?: ChatMentionProduct[];
  onMentionProductClick?: (product: ChatMentionProduct) => void;
}): JSX.Element {
  const { m, onOpenPdf, mentionProducts = [], onMentionProductClick } = props;
  const deleted = Boolean(m.deleted);
  const shielded = Boolean(m.shielded);
  const modHint = moderationHint(m);
  const strikeSelfRejected = companionIsSelfModerationRejected(m);
  const blockCls = strikeSelfRejected
    ? "h5-crm-msg-block h5-crm-msg-block--self-rejected"
    : "h5-crm-msg-block";
  const mentionInline = useMemo(
    () =>
      onMentionProductClick
        ? (text: string) =>
            renderChatMentionText(text, mentionProducts, {
              onProductClick: onMentionProductClick,
            })
        : undefined,
    [mentionProducts, onMentionProductClick]
  );

  if ((m.messageType === "IMAGE" || m.messageType === "STICKER") && m.fileUrl?.trim()) {
    const raw = resolveAppMediaUrl(m.fileUrl) ?? m.fileUrl;
    const src = /\.gif($|\?)/i.test(raw) ? stripOssImageProcess(raw) : raw;
    const thumbH = COMPANION_MSG_THUMB.height;
    const alt =
      m.messageType === "STICKER"
        ? displayFileName(m) || "表情"
        : displayFileName(m) || "图片";
    return (
      <div className={blockCls}>
        {deleted ? (
          <span className="h5-crm-msg-deleted">已撤回</span>
        ) : shielded ? (
          <span className="h5-crm-msg-shielded">已屏蔽</span>
        ) : (
          <>
            <div
              className="h5-crm-msg-thumb"
              style={{
                height: thumbH,
                background: COMPANION_MSG_THUMB.skeletonBg,
              }}
            >
              <Image
                src={src}
                alt={alt}
                className="h5-crm-msg-image"
                height={thumbH}
                onClick={(e) => e.stopPropagation()}
                preview={{
                  mask: false,
                  destroyOnHidden: true,
                  zIndex: 2000,
                }}
              />
            </div>
            {modHint ? <div className="h5-crm-msg-mod-hint">{modHint}</div> : null}
          </>
        )}
      </div>
    );
  }

  if (m.messageType === "PDF" && m.fileUrl?.trim()) {
    const name = displayFileName(m);
    const url = resolveAppMediaUrl(m.fileUrl) ?? m.fileUrl;
    return (
      <div className={blockCls}>
        {deleted ? (
          <span className="h5-crm-msg-deleted">已撤回</span>
        ) : shielded ? (
          <span className="h5-crm-msg-shielded">已屏蔽</span>
        ) : (
          <>
            <button
              type="button"
              className="h5-crm-pdf-card"
              onClick={(e) => {
                e.stopPropagation();
                onOpenPdf?.(url, name || "PDF");
              }}
            >
              <FilePdfOutlined className="h5-crm-pdf-ic" />
              <span className="h5-crm-pdf-name">{name || "PDF"}</span>
            </button>
            {modHint ? <div className="h5-crm-msg-mod-hint">{modHint}</div> : null}
          </>
        )}
      </div>
    );
  }

  return (
    <div className={blockCls}>
      {deleted ? (
        <span className="h5-crm-msg-deleted">已撤回</span>
      ) : shielded ? (
        <span className="h5-crm-msg-shielded">已屏蔽</span>
      ) : (
        <>
          <CsMessageTextContent content={m.content ?? ""} renderInline={mentionInline} />
          {modHint ? <div className="h5-crm-msg-mod-hint">{modHint}</div> : null}
        </>
      )}
    </div>
  );
}
